/**
 * ONBOARDING study_config contract (shared verbatim with the API + Diapason).
 * A researcher picks the signup onboarding mode in Diapason; it is stored as a
 * JSON string in the ONBOARDING study_config and, when present + valid, wins over
 * the per-project default (src/projects). Absent/invalid -> null (project default).
 *
 *   { "mode": "video" | "swiper" | "page" | "none", "videoIds"?: string[] }
 *
 * `videoIds` are Cloudflare Stream ids, only used for mode "video" (video
 * onboarding is researcher-authored — there is no code-configured video).
 */

export type OnboardingMode = "video" | "swiper" | "page" | "none";

export const ONBOARDING_MODES: OnboardingMode[] = [
  "video",
  "swiper",
  "page",
  "none",
];

export interface OnboardingConfig {
  mode: OnboardingMode;
  videoIds?: string[];
}

/** Parse the ONBOARDING config string; null when absent/malformed (-> project default). */
export const parseOnboardingConfig = (
  value: string | undefined | null
): OnboardingConfig | null => {
  if (!value) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }

  const config = parsed as Record<string, unknown>;

  if (
    typeof config.mode !== "string" ||
    !ONBOARDING_MODES.includes(config.mode as OnboardingMode)
  ) {
    return null;
  }

  const videoIds = Array.isArray(config.videoIds)
    ? config.videoIds.filter(
        (id): id is string => typeof id === "string" && id.trim() !== ""
      )
    : undefined;

  return { mode: config.mode as OnboardingMode, videoIds };
};

/** The mode actually rendered by the OnBoarding step. 'custom' = a
 *  developer-provided onboarding component (real code, per project); 'none' =
 *  nothing to show (the step is skipped). */
export type ResolvedOnboardingMode = OnboardingMode | "custom";

export interface ResolvedOnboarding {
  mode: ResolvedOnboardingMode;
  videoIds?: string[];
  /** False when there is nothing to show → the onboarding step must be skipped. */
  willRender: boolean;
}

/**
 * Resolves what the signup onboarding step shows for a study. Onboarding content
 * (video, and EditorJS swiper/page) is researcher-authored: the ONBOARDING-STUDY
 * content's EXISTENCE is the master switch — no page => 'none' (skip). The single
 * hard-coded escape hatch is a project's own custom onboarding component
 * (`onboardingComponent`), used when a developer wants real code instead of
 * EditorJS content.
 */
export const resolveOnboarding = (params: {
  /** Parsed ONBOARDING study_config (app.onboarding), or null. */
  studyOnboarding: OnboardingConfig | null;
  /** True when an ONBOARDING-STUDY content exists for the study. */
  hasContent: boolean;
  /** True when the project ships a custom-coded onboarding component. */
  hasCustomComponent: boolean;
  /** Per-project default type for a page with no explicit type (src/projects). */
  projectStyle?: "page" | "swiper";
}): ResolvedOnboarding => {
  const { studyOnboarding, hasContent, hasCustomComponent, projectStyle } = params;

  let mode: ResolvedOnboardingMode;
  if (hasCustomComponent) {
    // Developer opted for real code instead of EditorJS content.
    mode = "custom";
  } else if (hasContent) {
    // Researcher created an onboarding page; its type is the study_config mode,
    // defaulting to the project's authored style (swiper) or a single page.
    mode =
      studyOnboarding?.mode ?? (projectStyle === "swiper" ? "swiper" : "page");
  } else {
    mode = "none";
  }

  // Video onboarding is researcher-authored only (no code-configured ids).
  const videoIds = mode === "video" ? studyOnboarding?.videoIds : undefined;

  const willRender =
    mode === "custom" ||
    (mode === "video" && !!videoIds?.length) ||
    ((mode === "swiper" || mode === "page") && hasContent);

  return { mode, videoIds, willRender };
};
